import { useState } from "react"
import { ButtonDestructive, ButtonPrimary } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { CopyButton } from "@nova/component-copy-button"
import { fetchApiGateway } from "@nova/fetch-api-gateway"
import {
    getGenericConfirmationModalComponent,
    useConfirmationModalContext,
} from "features/modals/WorkspaceConfirmationModalV2"

export function ApiTokenTabContent() {
    const [token, setToken] = useState<string | null>(null)
    const [isGenerating, setIsGenerating] = useState(false)
    const [isRevoking, setIsRevoking] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const confirmationModal = useConfirmationModalContext()

    const handleGenerateClick = async () => {
        setErrorMessage(null)
        setIsGenerating(true)
        try {
            const { token } = await fetchApiGateway("post /me/api-token")
            setToken(token)
        } catch {
            setErrorMessage("Something went wrong. Please try again.")
        } finally {
            setIsGenerating(false)
        }
    }

    const handleRevokeClick = () => {
        confirmationModal.confirm({
            RenderComponent: getGenericConfirmationModalComponent({
                title: "Revoke API Access?",
                message:
                    "This stops all existing tokens from being refreshed. They can be used until they naturally expire. New tokens created after the revoke are not affected.",
                confirmText: "Revoke",
                leftConfirmIcon: Icons.LinkOff,
            }),
            asyncConfirmCallback: async () => {
                setErrorMessage(null)
                setIsRevoking(true)
                try {
                    await fetchApiGateway("post /me/api-token/revoke")
                    setToken(null)
                } catch {
                    setErrorMessage("Something went wrong. Please try again.")
                } finally {
                    setIsRevoking(false)
                }
            },
        })
    }

    return (
        <div className="flex flex-col gap-8 p-6">
            <div className="flex flex-col text-gray-100 gap-4 text-[14px]">
                <p>
                    Generate a token to authenticate requests to Nova&apos;s processing API routes.
                </p>
                <p>
                    The token expires 24 hours after it is generated. Refresh an active token via
                    the API or generate a new one here. Revoking prevents all existing tokens from
                    being refreshed, but they may still be used until they expire.
                </p>
                <p>
                    This token is tied to your user account, not a workspace. It carries access to
                    every workspace and project that you do.
                </p>
            </div>
            <div className="flex gap-4">
                <ButtonDestructive
                    title="Revoke Tokens"
                    LeftIcon={Icons.LinkOff}
                    disabled={isRevoking}
                    onClick={handleRevokeClick}
                    className="w-36"
                    isDestructive
                    isTransparent
                />
                <ButtonPrimary
                    title="Create Token"
                    LeftIcon={Icons.Key}
                    disabled={isGenerating || !!token}
                    onClick={handleGenerateClick}
                    className="w-36"
                />
            </div>
            {token && (
                <div className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded p-3">
                    <code className="flex-1 text-sm text-gray-0 break-all">{token}</code>
                    <CopyButton value={token} />
                </div>
            )}
            {errorMessage && <p className="text-sm text-red-400">{errorMessage}</p>}
        </div>
    )
}
